import { useEffect, useRef } from "react";

const DATE_PICKER_TAG = "goa-date-picker";
const BLOCK_TAG = "goa-block";

/**
 * GoabDatePicker's `type="input"` variant renders month/day/year fields side
 * by side inside an internal `goa-block` in its Shadow DOM. That block has no
 * responsive behaviour of its own currently, so the fields overflow a narrow viewport instead of
 * wrapping. External CSS can't reach into the Shadow DOM to fix this.
 *
 * This hook pierces the Shadow DOM (via the exposed `goa-block`
 * custom element property) to change the internal
 * block's `direction` between "row" (desktop) and "column" (mobile), so the
 * fields stack vertically instead of overflowing.
 *
 * Usage: attach the returned ref to a wrapping element around the
 * `GoabDatePicker type="input"` control.
 */
export function useStackDatePickerFieldsOnMobile(isMobile) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const container = containerRef.current;
    const hostElement = container?.querySelector(DATE_PICKER_TAG);
    if (!hostElement) {
      return;
    }

    const applyStack = () => {
      const blockElement = hostElement.shadowRoot?.querySelector(BLOCK_TAG);
      if (!blockElement) {
        return false;
      }
      blockElement.direction = isMobile ? "column" : "row";
      return true;
    };

    if (applyStack()) {
      return;
    }

    // The Shadow DOM content may not be rendered yet (e.g. the custom element
    // upgrade hasn't completed). Watch for it and apply once it appears.
    let observer;
    const observeShadowRoot = () => {
      if (!hostElement.shadowRoot) {
        return;
      }
      observer = new MutationObserver(() => {
        if (applyStack()) {
          observer.disconnect();
        }
      });
      observer.observe(hostElement.shadowRoot, {
        childList: true,
        subtree: true,
      });
    };

    if (hostElement.shadowRoot) {
      observeShadowRoot();
    } else if (typeof customElements !== "undefined") {
      customElements.whenDefined(DATE_PICKER_TAG).then(observeShadowRoot);
    }

    return () => observer?.disconnect();
  }, [isMobile]);

  return containerRef;
}

export default useStackDatePickerFieldsOnMobile;
